/**
 * Weather Section Component
 * Displays weather information for popular destinations with 3-day forecasts
 */

import { DestinationWeather, WeatherSection } from '../types/informational';
import { InformationalDataService } from '../services/InformationalDataService';

export class WeatherSectionComponent implements WeatherSection {
  public title: string;
  public subtitle: string;
  public destinations: DestinationWeather[];
  public showForecast: boolean;
  public forecastDays: number;
  
  private container: HTMLElement | null = null;
  private dataService: InformationalDataService;

  constructor(
    title: string = 'Destination Weather',
    subtitle: string = 'Check current weather conditions and forecasts',
    forecastDays: number = 3,
    showForecast: boolean = true
  ) {
    this.title = title;
    this.subtitle = subtitle;
    this.forecastDays = forecastDays;
    this.showForecast = showForecast;
    this.dataService = InformationalDataService.getInstance();
    this.destinations = this.dataService.getDestinationWeather().slice(0, 6);
  }

  /**
   * Initialize the component
   */
  public init(): void {
    this.createContainer();
    this.render();
    this.bindEvents();
    this.startAutoRefresh();
  }

  /**
   * Create the container element
   */
  private createContainer(): void {
    // Find insertion point in dynamic sections container
    const dynamicContainer = document.getElementById('dynamic-sections-container');
    if (!dynamicContainer) {
      console.error('Weather: Dynamic sections container not found');
      return;
    }

    // Create container
    this.container = document.createElement('section');
    this.container.className = 'weather-section';
    this.container.id = 'weather-section';
    this.container.setAttribute('aria-labelledby', 'weather-title');

    // Append to dynamic container
    dynamicContainer.appendChild(this.container);
  }

  /**
   * Render the component
   */
  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="weather-section__container">
        <header class="weather-section__header">
          <h2 class="weather-section__title" id="weather-title">
            ${this.escapeHtml(this.title)}
          </h2>
          <p class="weather-section__subtitle">
            ${this.escapeHtml(this.subtitle)}
          </p>
          <div class="weather-section__last-updated">
            Last update : ${this.formatTime(new Date())}
          </div>
        </header>

        <div class="weather-section__grid" role="region" aria-label="Weather information by destination">
          ${this.destinations.map(destWeather => this.renderWeatherCard(destWeather)).join('')}
        </div>

        <div class="weather-section__disclaimer">
          <p class="weather-section__disclaimer-text">
            ℹ️ Simulated weather data for demonstration purposes
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Render a single weather card
   */
  private renderWeatherCard(destWeather: DestinationWeather): string {
    const { destination, weather } = destWeather;
    const current = weather.current;
    
    const forecastHtml = this.showForecast ? `
      <div class="weather-card__forecast">
        <h4 class="weather-card__forecast-title">3-day forecast</h4>
        <div class="weather-card__forecast-days">
          ${weather.forecast.map(day => `
            <div class="weather-forecast-day">
              <div class="weather-forecast-day__date">
                ${this.formatDate(day.date)}
              </div>
              <div class="weather-forecast-day__icon">
                ${day.icon}
              </div>
              <div class="weather-forecast-day__temps">
                <span class="weather-forecast-day__high">${day.high}°</span>
                <span class="weather-forecast-day__low">${day.low}°</span>
              </div>
              <div class="weather-forecast-day__condition">
                ${this.escapeHtml(day.condition)}
              </div>
              ${day.precipitation > 20 ? `
                <div class="weather-forecast-day__precipitation">
                  🌧️ ${day.precipitation}%
                </div>
              ` : ''}
            </div>
          `).join('')}
        </div>
      </div>
    ` : '';

    return `
      <article class="weather-card" data-destination-id="${destination.id}">
        <div class="weather-card__header">
          <h3 class="weather-card__destination">
            ${this.escapeHtml(destination.name)}
          </h3>
          <p class="weather-card__country">
            ${this.escapeHtml(destination.country)}
          </p>
        </div>

        <div class="weather-card__current">
          <div class="weather-card__current-main">
            <div class="weather-card__temperature">
              ${current.temperature}°C
            </div>
            <div class="weather-card__condition">
              <span class="weather-card__condition-icon">${current.icon}</span>
              <span class="weather-card__condition-text">${this.escapeHtml(current.condition)}</span>
            </div>
          </div>
          
          <div class="weather-card__details">
            <div class="weather-card__detail">
              <span class="weather-card__detail-label">Humidity</span>
              <span class="weather-card__detail-value">${current.humidity}%</span>
            </div>
            <div class="weather-card__detail">
              <span class="weather-card__detail-label">Wind</span>
              <span class="weather-card__detail-value">${current.windSpeed} km/h</span>
            </div>
          </div>
        </div>

        ${forecastHtml}

        <div class="weather-card__actions">
          <button 
            class="weather-card__search-btn"
            type="button"
            data-destination-id="${destination.id}"
            aria-label="Rechercher des vols pour ${this.escapeHtml(destination.name)}"
          >
            Find flights
          </button>
        </div>
      </article>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.container) return;

    // Handle search button clicks
    this.container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const searchButton = target.closest('.weather-card__search-btn') as HTMLElement;

      if (searchButton) {
        const destinationId = searchButton.getAttribute('data-destination-id');
        if (destinationId) {
          this.handleDestinationSearch(destinationId);
        }
      }
    });

    // Handle keyboard navigation
    this.container.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        const target = event.target as HTMLElement;
        if (target.classList.contains('weather-card__search-btn')) {
          event.preventDefault();
          target.click();
        }
      }
    });
  }

  /**
   * Handle destination search
   */
  private handleDestinationSearch(destinationId: string): void {
    const destWeather = this.destinations.find(dw => dw.destination.id === destinationId);
    if (!destWeather) return;

    const destination = destWeather.destination;

    // Fill the search form with destination data
    const destinationInput = document.getElementById('destination') as HTMLInputElement;
    if (destinationInput) {
      destinationInput.value = destination.name;
      destinationInput.focus();
      
      // Scroll to search form
      const heroSection = document.querySelector('.hero');
      if (heroSection) {
        heroSection.scrollIntoView({ behavior: 'smooth' });
      }
    }

    console.log(`Searching flights for ${destination.name} (current weather: ${destWeather.weather.current.temperature}°C)`);
  }

  /**
   * Start auto-refresh for weather data
   */
  private startAutoRefresh(): void {
    // Refresh weather data every 30 minutes
    setInterval(() => {
      this.refreshWeatherData();
    }, 30 * 60 * 1000);
  }

  /**
   * Refresh weather data
   */
  private refreshWeatherData(): void {
    this.destinations = this.dataService.getDestinationWeather().slice(0, 6);
    this.render();
    console.log('Weather data refreshed');
  }

  /**
   * Format date for forecast
   */
  private formatDate(date: Date): string {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  }

  /**
   * Format time for last updated
   */
  private formatTime(date: Date): string {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  /**
   * Update weather data
   */
  public updateWeatherData(destinations: DestinationWeather[]): void {
    this.destinations = destinations;
    this.render();
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Destroy the component
   */
  public destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}